import { type FC, type MouseEvent } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import serverVars from 'server-vars';
import { Link } from 'dibs-elements/exports/Link';
import { isMobile } from 'dibs-media-query';
import { trackNavInteraction } from '../../helpers/tracking';
import GetHelpModalSellerLazy from 'dibs-contact-1stdibs/exports/GetHelpModalSellerLazy';
import {
    use1stdibsContactModalOpenState,
    useSendFeedbackModalOpenState,
} from '../NavItems/helpers';
import { SendFeedbackModal } from '../NavItems/SendFeedbackModal';
import { SERVER_VARS } from '../../../../server/constants';
import { useNavContext } from '../../NavContext';
import SV from 'server-vars';

import styles from './styles/NavSupportItems.scss';
import classNames from 'classnames';
import dibsCss from 'dibs-css';

const messages = defineMessages({
    supportCenter: {
        id: 'useSupportItems.supportCenter',
        defaultMessage: 'Support Center',
    },
    contact1stdibs: {
        id: 'useSupportItems.contact1stdibs',
        defaultMessage: 'Contact 1stDibs',
    },
    sendFeedback: {
        id: 'useSupportItems.sendFeedback',
        defaultMessage: 'Send Feedback',
    },
});

const SupportCenterLink: FC<{
    href: string;
    title: string;
    eventName: string;
    interactionType: string;
    handler?: (e: MouseEvent<HTMLAnchorElement>) => void;
    dataTn?: string;
}> = ({ href, title, eventName, interactionType, handler, dataTn }) => {
    const isMobileView = isMobile();

    return (
        <Link
            href={href || ''}
            onClick={e => {
                if (handler) {
                    handler(e as MouseEvent<HTMLAnchorElement>);
                } else {
                    trackNavInteraction({
                        action: eventName,
                        label: eventName,
                        interactionType: interactionType,
                    });
                }
            }}
            dataTn={dataTn}
        >
            <span
                className={classNames(styles.supportLink, dibsCss.textGray500, {
                    [dibsCss.sassyFontBodyMediumHeavy]: isMobileView,
                    [dibsCss.textGray500]: !isMobileView,
                    [dibsCss.textSatan]: isMobileView,
                })}
            >
                {title}
            </span>
        </Link>
    );
};

export const NavSupportItems: FC = () => {
    const { isContactModalOpen, setIsContactModalOpen, contactModalOpenHandler } =
        use1stdibsContactModalOpenState('/dealers/contact/');
    const { isSendFeedbackModalOpen, setIsSendFeedbackModalOpen, sendFeedbackModalOpenHandler } =
        useSendFeedbackModalOpenState();
    const intl = useIntl();
    const { status } = useNavContext();

    const isMobileView = isMobile();
    const locale = intl.locale;
    const supportCenterHost = SV.get(SERVER_VARS.NODE_SELLER_SUPPORT_CENTER_HOST);

    const feedBackSurveyUrl = serverVars.get(SERVER_VARS.constants)?.feedBackSurveyUrl;

    const wrapperClassName = isMobileView
        ? classNames(
              dibsCss.gapSmall,
              dibsCss.mxSmall,
              dibsCss.mtXsmall,
              dibsCss.pxXsmall,
              dibsCss.ptSmall,
              dibsCss.pbXsmall,
              dibsCss.border0,
              dibsCss.borderT,
              dibsCss.borderSolid,
              dibsCss.borderDolphin,
              { [dibsCss.hidden]: status === 'collapsed' }
          )
        : dibsCss.gapXsmall;

    return (
        <div
            className={classNames(
                dibsCss.flex,
                dibsCss.flexCol,
                dibsCss.itemsStart,
                wrapperClassName
            )}
        >
            <SupportCenterLink
                href={handleLocaleUrl(supportCenterHost, locale)}
                title={intl.formatMessage(messages.supportCenter)}
                eventName={messages.supportCenter.defaultMessage}
                interactionType="drop down"
                dataTn="support-center"
            />
            <SupportCenterLink
                href="/dealers/contact/"
                title={intl.formatMessage(messages.contact1stdibs)}
                eventName={messages.contact1stdibs.defaultMessage}
                interactionType="drop down"
                handler={contactModalOpenHandler}
                dataTn="contact-1stdibs"
            />
            <GetHelpModalSellerLazy
                isOpen={isContactModalOpen}
                onClose={() => setIsContactModalOpen(false)}
                placement="dealerGlobalNav"
            />
            <SupportCenterLink
                href="#send-feedback-modal"
                title={intl.formatMessage(messages.sendFeedback)}
                eventName={messages.sendFeedback.defaultMessage}
                interactionType="drop down"
                handler={sendFeedbackModalOpenHandler}
                dataTn="send-feedback"
            />
            <SendFeedbackModal
                isOpen={isSendFeedbackModalOpen}
                onClose={() => setIsSendFeedbackModalOpen(false)}
                feedBackSurveyUrl={feedBackSurveyUrl}
            />
        </div>
    );
};
